import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";
import { Icon, type IconName } from "./icon";
import s from "./ui.module.css";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

export function Page({ title, crumbs, lead, children }: { title: string; crumbs?: { label: string; href?: string }[]; lead?: ReactNode; children?: ReactNode }) {
  return (
    <div className={s.page}>
      {crumbs && <Breadcrumbs items={crumbs} />}
      <h1 className={s.pageTitle}>{title}</h1>
      {lead && <p className={s.lead}>{lead}</p>}
      {children}
    </div>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="現在の場所" className={cx(s.crumbs, "no-print")}>
      <ol>
        {items.map((c, i) => (
          <li key={c.label}>{c.href && i < items.length - 1 ? <Link href={c.href}>{c.label}</Link> : <span aria-current={i === items.length - 1 ? "page" : undefined}>{c.label}</span>}</li>
        ))}
      </ol>
    </nav>
  );
}

type ButtonLook = "primary" | "secondary" | "line" | "danger";
const look = (v: ButtonLook) => cx(s.button, v === "primary" && s.primary, v === "line" && s.line, v === "danger" && s.danger);

export function Button({ variant = "secondary", icon, block, children, className, ...rest }: ComponentProps<"button"> & { variant?: ButtonLook; icon?: IconName; block?: boolean }) {
  return (
    <button type="button" {...rest} className={cx(look(variant), block && s.block, className)}>
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export function ButtonLink({ variant = "secondary", icon, block, children, href, download, external }: { variant?: ButtonLook; icon?: IconName; block?: boolean; children: ReactNode; href: string; download?: boolean; external?: boolean }) {
  const cls = cx(look(variant), block && s.block);
  // PDF のダウンロードなど、画面遷移ではないものは普通の a
  if (download || external) return <a className={cls} href={href} download={download || undefined}>{icon && <Icon name={icon} />}{children}</a>;
  return <Link className={cls} href={href}>{icon && <Icon name={icon} />}{children}</Link>;
}

export function TextLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link className={s.linkButton} href={href}>
      <Icon name="chevron_right" />
      {children}
    </Link>
  );
}

export function Actions({ children }: { children: ReactNode }) {
  return <div className={cx(s.actions, "no-print")}>{children}</div>;
}

export function Status({ tone = "info", children }: { tone?: "info" | "ok" | "warn" | "error"; children: ReactNode }) {
  const icon: IconName = tone === "ok" ? "check_circle" : tone === "warn" ? "warning" : tone === "error" ? "error" : "info";
  return (
    <div className={cx(s.status, s[tone])} role={tone === "error" ? "alert" : "status"}>
      <Icon name={icon} filled />
      <div>{children}</div>
    </div>
  );
}

export function Section({ title, link, children, headingLevel = 2 }: { title?: ReactNode; link?: { href: string; label: string }; children: ReactNode; headingLevel?: 2 | 3 }) {
  const H = headingLevel === 2 ? "h2" : "h3";
  return (
    <section className={s.section}>
      {title && (
        <div className={s.sectionHeader}>
          <H className={s.sectionTitle}>{title}</H>
          {link && <Link className={cx(s.sectionLink, "no-print")} href={link.href}>{link.label}<Icon name="chevron_right" size={20} /></Link>}
        </div>
      )}
      {children}
    </section>
  );
}

export function SectionBody({ children }: { children: ReactNode }) {
  return <div className={s.sectionBody}>{children}</div>;
}

export function Rows({ children, empty }: { children: ReactNode; empty?: string }) {
  const list = Array.isArray(children) ? children.filter(Boolean) : children ? [children] : [];
  if (list.length === 0) return <p className={s.empty}>{empty ?? "まだありません。"}</p>;
  return <ul className={s.rows}>{list.map((c, i) => <li key={i}>{c}</li>)}</ul>;
}

/** 罫線の行（一覧の1件）。押すと詳細へ */
export function ListRow({ href, title, sub, amount, isNew, stack }: { href: string; title: ReactNode; sub?: ReactNode; amount?: string; isNew?: boolean; stack?: boolean }) {
  return (
    <Link className={cx(s.row, stack && s.stack)} href={href}>
      <span className={s.rowMain}>
        <span className={s.rowTitle}>{title}</span>
        {sub && <span className={s.rowSub}>{sub}</span>}
      </span>
      <span className={s.rowEnd}>
        {amount && <span className={s.rowAmount}>{amount}</span>}
        {isNew && <span className={s.newMark}>新着</span>}
      </span>
      <Icon name="chevron_right" className={s.chev} />
    </Link>
  );
}

export function KeyValues({ items }: { items: ({ label: string; value: ReactNode; big?: boolean } | false | null)[] }) {
  return (
    <dl className={s.kv}>
      {items.filter((x): x is { label: string; value: ReactNode; big?: boolean } => Boolean(x)).map((i) => (
        <div key={i.label}>
          <dt>{i.label}</dt>
          <dd className={i.big ? s.amountLg : undefined}>{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export const styles = s;
